// src/stores/accessibilityStore.js
import { defineStore } from 'pinia';

const STORAGE_KEY = 'accessibility.v1';

const defaultSettings = {
  fontSize: 100,           // Percentage: 100, 125, 150
  highContrast: false,     // High contrast mode
  lineSpacing: false,      // Increased line spacing
  letterSpacing: false,    // Increased letter spacing
  highlightLinks: false,   // Underline all links
  stopAnimations: false,   // Reduce motion
  readingRuler: false,     // Reading ruler follows mouse
  largeCursor: false,      // Large cursor
  focusHighlight: false,   // Strong focus indicators
  readableFont: false,     // Switch to readable font
  saturation: 100,         // Saturation percentage: 0, 50, 100, 150
};

export const useAccessibilityStore = defineStore('accessibility', {
  state: () => ({
    ...defaultSettings,
    isOpen: false,
    rulerY: 0,
  }),

  getters: {
    hasActiveSettings: (state) => {
      return (
        state.fontSize !== 100 ||
        state.highContrast ||
        state.lineSpacing ||
        state.letterSpacing ||
        state.highlightLinks ||
        state.stopAnimations ||
        state.readingRuler ||
        state.largeCursor ||
        state.focusHighlight ||
        state.readableFont ||
        state.saturation !== 100
      );
    },
  },

  actions: {
    toggle() {
      this.isOpen = !this.isOpen;
    },

    close() {
      this.isOpen = false;
    },

    increaseFontSize() {
      if (this.fontSize < 150) {
        this.fontSize += 25;
        this.applySettings();
        this.saveToStorage();
      }
    },

    decreaseFontSize() {
      if (this.fontSize > 100) {
        this.fontSize -= 25;
        this.applySettings();
        this.saveToStorage();
      }
    },

    increaseSaturation() {
      if (this.saturation < 150) {
        this.saturation += 50;
        this.applySettings();
        this.saveToStorage();
      }
    },

    decreaseSaturation() {
      if (this.saturation > 0) {
        this.saturation -= 50;
        this.applySettings();
        this.saveToStorage();
      }
    },

    toggleSetting(setting) {
      if (setting in this.$state && typeof this.$state[setting] === 'boolean') {
        this.$state[setting] = !this.$state[setting];
        this.applySettings();
        this.saveToStorage();
      }
    },

    updateRulerPosition(y) {
      this.rulerY = y;
    },

    reset() {
      Object.assign(this.$state, defaultSettings);
      this.applySettings();
      this.saveToStorage();
    },

    applySettings() {
      const html = document.documentElement;
      const body = document.body;

      // Font size
      html.style.fontSize = `${this.fontSize}%`;

      // High contrast
      body.classList.toggle('a11y-high-contrast', this.highContrast);

      // Line spacing
      body.classList.toggle('a11y-line-spacing', this.lineSpacing);

      // Letter spacing
      body.classList.toggle('a11y-letter-spacing', this.letterSpacing);

      // Highlight links
      body.classList.toggle('a11y-highlight-links', this.highlightLinks);

      // Stop animations
      body.classList.toggle('a11y-stop-animations', this.stopAnimations);

      // Reading ruler
      body.classList.toggle('a11y-reading-ruler', this.readingRuler);

      // Large cursor
      body.classList.toggle('a11y-large-cursor', this.largeCursor);

      // Focus highlight
      body.classList.toggle('a11y-focus-highlight', this.focusHighlight);

      // Readable font
      body.classList.toggle('a11y-readable-font', this.readableFont);

      // Saturation
      html.style.setProperty('--a11y-saturation', `${this.saturation}%`);
      body.classList.toggle('a11y-saturation', this.saturation !== 100);

      // Apply combined filter to body (excluding panel via CSS)
      const filters = [];
      if (this.highContrast) filters.push('contrast(1.4)');
      if (this.saturation !== 100) filters.push(`saturate(${this.saturation}%)`);
      body.style.setProperty('--a11y-filter', filters.length ? filters.join(' ') : 'none');
    },

    saveToStorage() {
      const settings = {
        fontSize: this.fontSize,
        highContrast: this.highContrast,
        lineSpacing: this.lineSpacing,
        letterSpacing: this.letterSpacing,
        highlightLinks: this.highlightLinks,
        stopAnimations: this.stopAnimations,
        readingRuler: this.readingRuler,
        largeCursor: this.largeCursor,
        focusHighlight: this.focusHighlight,
        readableFont: this.readableFont,
        saturation: this.saturation,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    },

    loadFromStorage() {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const settings = JSON.parse(stored);
          Object.assign(this.$state, settings);
        }
        this.applySettings();
      } catch (e) {
        console.error('Failed to load accessibility settings:', e);
      }
    },

    init() {
      this.loadFromStorage();
    },
  },
});
